import { useState, type CSSProperties } from 'react';

// One punch (entrée or sortie) that can be corrected in place. Click the
// time → a time field; Enter or leaving the field saves, Échap cancels.
//
// A correction never erases what the pointeuse said: when the day's time
// differs from the device reading, the device time stays beside it,
// struck through. Typing the device time back undoes the correction —
// the server clears the « man. » flag when both agree again.

interface Props {
  label: 'entrée' | 'sortie';
  value: string | null;
  /** What the pointeuse recorded (null = no device punch on that side). */
  device: string | null;
  manual: boolean;
  /** False on a signed-off day, or when the row is read-only. */
  editable: boolean;
  pending?: boolean;
  onSave: (value: string) => void;
  testid: string;
  /** Size of the time itself — the arbitrage readout is larger. */
  size?: 'row' | 'hero';
  style?: CSSProperties;
}

export function PunchTime({ label, value, device, manual, editable, pending, onSave, testid, size = 'row', style }: Props) {
  const [editing, setEditing] = useState(false);
  const ofLabel = label === 'entrée' ? "d'entrée" : 'de sortie';
  const corrected = manual && device != null && device !== value;

  const commit = (v: string) => {
    setEditing(false);
    if (v && v !== value) onSave(v);
  };

  if (editing) {
    return (
      <span className={`punch-edit ${size}`} onClick={(e) => e.stopPropagation()}>
        <input
          type="time"
          autoFocus
          defaultValue={value ?? ''}
          className="entry-input punch-input"
          disabled={pending}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            // Stop here: the arbitrage listens for Enter / Échap on the
            // window, and neither should reach it from this field.
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              (e.target as HTMLInputElement).blur();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              setEditing(false);
            }
          }}
          data-testid={`${testid}-input`}
          aria-label={`Corriger l'heure ${ofLabel}`}
        />
        {corrected && (
          <button
            type="button"
            className="punch-restore"
            // mousedown, not click: the field's blur would save first.
            onMouseDown={(e) => {
              e.preventDefault();
              setEditing(false);
              if (device) onSave(device);
            }}
            data-testid={`${testid}-restore`}
            title="Revenir à l'heure de la pointeuse"
          >
            ↺ {device}
          </button>
        )}
      </span>
    );
  }

  const body = (
    <>
      {value ?? '—'}
      {manual && <sup className="punch-man">man.</sup>}
      {corrected && (
        <s className="punch-device" data-testid={`${testid}-device`}>
          {device}
        </s>
      )}
    </>
  );
  const title = corrected
    ? `Pointeuse : ${device} — corrigé à la main.${editable ? ' Cliquer pour modifier.' : ''}`
    : manual
      ? `Saisie manuelle.${editable ? ' Cliquer pour modifier.' : ''}`
      : editable
        ? `Corriger l'heure ${ofLabel}`
        : undefined;

  if (!editable) {
    return (
      <span className={`punch-time ${size}`} title={title} style={style} data-testid={testid}>
        {body}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={`punch-time editable ${size}`}
      title={title}
      style={style}
      disabled={pending}
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      data-testid={testid}
      aria-label={`${label} ${value ?? 'absente'}${corrected ? `, pointeuse ${device}` : ''} — corriger`}
    >
      {body}
    </button>
  );
}
